/**
 * Phase 11 — Team Management API helpers + UI label maps.
 *
 * All exported functions return raw response data (no axios envelope) so
 * callers can `await getTeamMember(id)` directly.
 */
import api from "./api";

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const listTeam = (params = {}) =>
  api.get("/team", { params }).then((r) => r.data);

export const getOwnProfile = () => api.get("/team/me").then((r) => r.data);

export const getTeamMember = (userId) =>
  api.get(`/team/${userId}`).then((r) => r.data);

export const createTeamMember = (body) =>
  api.post("/team", body).then((r) => r.data);

export const updateTeamMember = (userId, body) =>
  api.patch(`/team/${userId}`, body).then((r) => r.data);

export const deactivateTeamMember = (userId) =>
  api.patch(`/team/${userId}/deactivate`).then((r) => r.data);

export const reactivateTeamMember = (userId) =>
  api.patch(`/team/${userId}/reactivate`).then((r) => r.data);

export const resetTeamMemberPassword = (userId) =>
  api.post(`/team/${userId}/reset-password`).then((r) => r.data);

// Phase 11.1 — self-service password change for the current viewer.
export const changeOwnPassword = ({ current_password, new_password }) =>
  api
    .post("/team/me/change-password", { current_password, new_password })
    .then((r) => r.data);

// ---------------------------------------------------------------------------
// UI label / style maps
// ---------------------------------------------------------------------------

export const ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "ops", label: "Operations" },
  { value: "lead_tech", label: "Lead Tech" },
  { value: "tech_assistant", label: "Tech Assistant" },
];

export const ROLE_LABEL = {
  owner: "Owner",
  ops: "Operations",
  lead_tech: "Lead Tech",
  tech_assistant: "Tech Assistant",
};

// Tints used by TeamRoleBadge — purple/blue/teal/slate per spec.
export const ROLE_TINT = {
  owner: "bg-purple-100 text-purple-900 border-purple-300",
  ops: "bg-blue-100 text-blue-900 border-blue-300",
  lead_tech: "bg-teal-100 text-teal-900 border-teal-300",
  tech_assistant: "bg-slate-100 text-slate-700 border-slate-300",
};

export const DAYS_OF_WEEK = [
  { value: "monday", short: "Mon" },
  { value: "tuesday", short: "Tue" },
  { value: "wednesday", short: "Wed" },
  { value: "thursday", short: "Thu" },
  { value: "friday", short: "Fri" },
  { value: "saturday", short: "Sat" },
  { value: "sunday", short: "Sun" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function relativeTime(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const ms = Date.now() - dt.getTime();
  if (ms < 0) return dt.toLocaleString();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

export function isoDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

export const PHONE_PLACEHOLDER = "+14155551234";
